import {
  commissionItemKindLabels,
  commissionPackageModeLabels,
  COMMISSION_PACKAGE_MODES,
  type CommissionItemKind,
  type CommissionPackageMode,
  type CommissionRate,
  type CommissionRateType,
} from '@hue/contracts';
import {
  BackToTop,
  Button,
  CurrencyInput,
  Input,
  Modal,
  SegmentedControl,
  Select,
  Switch,
  formatCurrency,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Percent, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  EmptyCard,
  ErrorCard,
  LoadingCard,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import { PageHeader } from '../../app/page-title.js';
import {
  commissionsApi,
  getApiError,
  type CommissionCatalogItem,
  type CommissionReceiver,
} from '../../lib/api.js';
import { commissionRateLabel } from './commission-rate-label.js';
import { parsePercent } from './sale-cart.js';

const PAGE_SIZE = 25;
const RATE_TYPES: ReadonlyArray<{ value: CommissionRateType; label: string }> = [
  { value: 'PERCENT', label: 'Percentual' },
  { value: 'FIXED', label: 'Valor fixo' },
];
const ITEM_KINDS = (['SERVICE', 'PRODUCT', 'PACKAGE'] as const).map((value) => ({
  value,
  label: `${commissionItemKindLabels[value]}s`,
}));

function RateFields({
  label,
  rate,
  onChange,
}: {
  label: string;
  rate: CommissionRate;
  onChange: (rate: CommissionRate) => void;
}) {
  const [percentText, setPercentText] = useState(
    rate.rateType === 'PERCENT' && rate.rateValue
      ? String(rate.rateValue / 100).replace('.', ',')
      : '',
  );
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Select
        label={`Tipo de ${label}`}
        value={rate.rateType}
        options={[...RATE_TYPES]}
        onValueChange={(rateType) => {
          setPercentText('');
          onChange({ rateType: rateType as CommissionRateType, rateValue: 0 });
        }}
      />
      {rate.rateType === 'PERCENT' ? (
        <Input
          label={`Percentual de ${label}`}
          inputMode="decimal"
          value={percentText}
          onChange={(event) => {
            setPercentText(event.target.value);
            onChange({ ...rate, rateValue: parsePercent(event.target.value) });
          }}
        />
      ) : (
        <CurrencyInput
          label={`Valor de ${label} por unidade`}
          value={rate.rateValue}
          onValueChange={(rateValue) => onChange({ ...rate, rateValue: rateValue ?? 0 })}
        />
      )}
    </div>
  );
}

interface RuleForm extends CommissionRate {
  offers: boolean;
  packageMode: CommissionPackageMode;
  assistantBasisPoints: number | null;
  exceptions: Array<CommissionRate & { resourceId: string }>;
}

function formOf(item: CommissionCatalogItem): RuleForm {
  return {
    offers: item.rule !== null,
    rateType: item.rule?.rateType ?? 'PERCENT',
    rateValue: item.rule?.rateValue ?? 0,
    packageMode: item.rule?.packageMode ?? 'ON_SALE',
    assistantBasisPoints: item.rule?.assistantBasisPoints ?? null,
    exceptions: item.rule?.exceptions ?? [],
  };
}

function RuleDialog({
  item,
  receivers,
  onClose,
  onSaved,
}: {
  item: CommissionCatalogItem;
  receivers: readonly CommissionReceiver[];
  onClose: () => void;
  onSaved: (message: string) => Promise<void>;
}) {
  const [form, setForm] = useState(() => formOf(item));
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const available = receivers.filter(
    (receiver) => !form.exceptions.some((exception) => exception.resourceId === receiver.id),
  );

  async function save() {
    setError(undefined);
    if (form.offers && form.rateValue <= 0) {
      setError('Informe um percentual ou valor maior que zero.');
      return;
    }
    if (form.offers && form.exceptions.some((exception) => exception.rateValue <= 0)) {
      setError('Informe o percentual ou valor de cada exceção.');
      return;
    }
    setSaving(true);
    try {
      if (form.offers) {
        await commissionsApi.saveRule(item.itemKind, item.referenceId, {
          rateType: form.rateType,
          rateValue: form.rateValue,
          packageMode: item.itemKind === 'PACKAGE' ? form.packageMode : null,
          assistantBasisPoints: item.itemKind === 'SERVICE' ? form.assistantBasisPoints : null,
          exceptions: form.exceptions,
        });
        await onSaved('Regra de comissão salva.');
      } else {
        await commissionsApi.removeRule(item.itemKind, item.referenceId);
        await onSaved('O item não oferece mais comissão.');
      }
    } catch (cause) {
      setError(getApiError(cause));
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={item.displayName}
      description="Mudar a regra não altera vendas já feitas: cada venda guarda o retrato do momento."
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" disabled={saving} onClick={() => void save()}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {error ? <RequestAlert message={error} /> : null}
        <Switch
          checked={form.offers}
          onChange={(offers) => setForm((current) => ({ ...current, offers }))}
        >
          Oferece comissão
        </Switch>
        {form.offers ? (
          <>
            <RateFields
              label="comissão"
              rate={form}
              onChange={(rate) => setForm((current) => ({ ...current, ...rate }))}
            />
            {item.itemKind === 'PACKAGE' ? (
              <div className="grid gap-1.5">
                <Select
                  label="Quando a comissão nasce"
                  value={form.packageMode}
                  options={COMMISSION_PACKAGE_MODES.map((value) => ({
                    value,
                    label: commissionPackageModeLabels[value],
                  }))}
                  onValueChange={(packageMode) =>
                    setForm((current) => ({
                      ...current,
                      packageMode: packageMode as CommissionPackageMode,
                    }))
                  }
                />
                <p className="text-xs text-content-faint">
                  {form.packageMode === 'ON_SALE'
                    ? 'Quem vendeu recebe ao confirmar a venda; as sessões depois não geram comissão.'
                    : 'Quem atendeu recebe a cada sessão, sobre o valor dela no pacote. Sessão não usada não gera comissão.'}
                </p>
              </div>
            ) : null}
            {item.itemKind === 'SERVICE' ? (
              <Input
                label="Parte do assistente, em percentual"
                inputMode="decimal"
                hint="Sai da comissão de quem atendeu, e o custo da empresa não muda. Vazio: o serviço não tem assistente."
                defaultValue={
                  form.assistantBasisPoints
                    ? String(form.assistantBasisPoints / 100).replace('.', ',')
                    : ''
                }
                onChange={(event) => {
                  const assistantBasisPoints = parsePercent(event.target.value);
                  setForm((current) => ({
                    ...current,
                    assistantBasisPoints: assistantBasisPoints || null,
                  }));
                }}
              />
            ) : null}
            <fieldset className="grid gap-3">
              <legend className="mb-1 text-sm font-semibold">Exceção por profissional</legend>
              {form.exceptions.length === 0 ? (
                <p className="text-xs text-content-faint">
                  Sem exceção, todos os profissionais seguem a regra acima.
                </p>
              ) : null}
              {form.exceptions.map((exception) => {
                const name =
                  receivers.find((receiver) => receiver.id === exception.resourceId)?.displayName ??
                  'Profissional que não recebe mais';
                return (
                  <div
                    key={exception.resourceId}
                    className="grid gap-2 rounded-xl border border-surface-border p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        aria-label={`Retirar a exceção de ${name}`}
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            exceptions: current.exceptions.filter(
                              (one) => one.resourceId !== exception.resourceId,
                            ),
                          }))
                        }
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                    <RateFields
                      label={`comissão de ${name}`}
                      rate={exception}
                      onChange={(rate) =>
                        setForm((current) => ({
                          ...current,
                          exceptions: current.exceptions.map((one) =>
                            one.resourceId === exception.resourceId ? { ...one, ...rate } : one,
                          ),
                        }))
                      }
                    />
                  </div>
                );
              })}
              {available.length > 0 ? (
                <Select
                  label="Acrescentar exceção"
                  value=""
                  options={[
                    { value: '', label: 'Escolha o profissional' },
                    ...available.map((receiver) => ({
                      value: receiver.id,
                      label: receiver.displayName,
                    })),
                  ]}
                  onValueChange={(resourceId) => {
                    if (!resourceId) return;
                    setForm((current) => ({
                      ...current,
                      exceptions: [
                        ...current.exceptions,
                        { resourceId, rateType: 'PERCENT', rateValue: 0 },
                      ],
                    }));
                  }}
                />
              ) : null}
            </fieldset>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

/// O item oferece comissão ou não, e como (ADR 0082, decisões 1 e 24). A regra mora em Vendas, e não nos
/// cadastros de Serviços e Estoque, porque a comissão é assunto da venda.
function ProfessionalsSeeOwnSwitch() {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const key = ['company', 'commissions', 'unit-settings'];
  const settings = useQuery({
    queryKey: key,
    queryFn: commissionsApi.unitSettings,
    retry: false,
  });
  const [error, setError] = useState<string>();
  async function change(next: boolean) {
    try {
      await commissionsApi.saveUnitSettings(next);
      setError(undefined);
      snackbar.success(next ? 'Consulta liberada nesta unidade.' : 'Consulta desligada.');
      await queryClient.invalidateQueries({ queryKey: key });
    } catch (cause) {
      setError(getApiError(cause));
    }
  }
  if (!settings.data) return null;
  return (
    <div className="mb-4 grid gap-1 rounded-xl border border-border p-3">
      {error ? <RequestAlert message={error} /> : null}
      <Switch checked={settings.data.professionalsSeeOwn} onChange={(next) => void change(next)}>
        Profissional vê as próprias comissões
      </Switch>
      <p className="text-xs text-content-faint">
        Vale nesta unidade, para quem tem acesso ao sistema, está ligado ao cadastro do profissional
        e tem a permissão no perfil.
      </p>
    </div>
  );
}

export function CommissionRules({ showsUnitSetting = true }: { showsUnitSetting?: boolean }) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [itemKind, setItemKind] = useState<CommissionItemKind>('SERVICE');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<CommissionCatalogItem | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search.trim());
      setOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const catalog = useQuery({
    queryKey: ['company', 'commissions', 'catalog', itemKind, debounced, offset],
    queryFn: () =>
      commissionsApi.catalog({
        itemKind,
        limit: PAGE_SIZE,
        offset,
        ...(debounced ? { search: debounced } : {}),
      }),
    retry: false,
  });
  const receivers = useQuery({
    queryKey: ['company', 'commissions', 'receivers'],
    queryFn: commissionsApi.receivers,
    retry: false,
  });
  const items = catalog.data?.items ?? [];

  return (
    <section>
      <PageHeader
        trail="Configurações / Regras de comissão"
        title="Regras de comissão"
        description="Diga quais serviços, produtos e pacotes oferecem comissão, e quanto. Quem recebe é marcado na ficha do profissional, em Profissionais."
      />
      {showsUnitSetting ? <ProfessionalsSeeOwnSwitch /> : null}
      <div className="mb-4 grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-end">
        <SegmentedControl
          label="Tipo de item"
          compact
          options={ITEM_KINDS}
          value={itemKind}
          onChange={(next) => {
            setItemKind(next);
            setOffset(0);
          }}
        />
        <Input
          label="Buscar item"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {catalog.isLoading ? (
        <LoadingCard label="Carregando itens" />
      ) : catalog.error ? (
        <ErrorCard message={getApiError(catalog.error)} />
      ) : items.length === 0 ? (
        <EmptyCard
          icon={Percent}
          title="Nenhum item encontrado"
          description="Limpe a busca ou troque o tipo de item."
        />
      ) : (
        <ul className="divide-y divide-surface-border rounded-2xl border border-surface-border bg-surface-card px-4">
          {items.map((item) => (
            <li
              key={item.referenceId}
              className="flex flex-wrap items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{item.displayName}</p>
                <p className="text-xs text-content-faint">
                  {formatCurrency(item.priceCents, 'BRL')} ·{' '}
                  {item.rule
                    ? [
                        `Comissão ${commissionRateLabel(item.rule)}`,
                        item.rule.packageMode
                          ? commissionPackageModeLabels[item.rule.packageMode]
                          : null,
                        item.rule.exceptions.length
                          ? `${item.rule.exceptions.length} ${item.rule.exceptions.length === 1 ? 'exceção' : 'exceções'}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : 'Sem comissão'}
                </p>
              </div>
              <Button type="button" variant="outline" onClick={() => setEditing(item)}>
                {item.rule ? 'Alterar' : 'Definir'}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Pagination
        total={catalog.data?.total ?? 0}
        limit={PAGE_SIZE}
        offset={offset}
        onOffsetChange={setOffset}
        itemLabel={(count) => (count === 1 ? 'item' : 'itens')}
      />
      {editing ? (
        <RuleDialog
          item={editing}
          receivers={receivers.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={async (message) => {
            await queryClient.invalidateQueries({
              queryKey: ['company', 'commissions', 'catalog'],
            });
            setEditing(null);
            snackbar.success(message);
          }}
        />
      ) : null}
      <BackToTop />
    </section>
  );
}
